import React from 'react';
import { Box, Typography, Chip, useTheme, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';

interface TrafficLightProps {
  status: 'green' | 'yellow' | 'red' | 'grün' | 'gelb' | 'rot' | 1 | 2 | 3;
  score?: number;
  recommendation?: string;
  size?: 'small' | 'medium' | 'large';
}

// Pulsing animation for active light
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px currentColor, 0 0 40px currentColor, 0 0 60px currentColor;
  }
  50% {
    box-shadow: 0 0 30px currentColor, 0 0 60px currentColor, 0 0 90px currentColor;
  }
`;

const TrafficLightContainer = styled(Box)<{ size: 'small' | 'medium' | 'large' }>(({ size, theme }) => {
  const sizes = {
    small: { width: 100, height: 240, lightSize: 28 },
    medium: { width: 130, height: 300, lightSize: 36 },
    large: { width: 160, height: 360, lightSize: 44 }
  };

  const { width, height } = sizes[size];

  return {
    width: width,
    height: height,
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, rgba(40, 40, 50, 0.95) 0%, rgba(30, 30, 40, 0.95) 100%)'
      : 'linear-gradient(180deg, rgba(50, 50, 60, 0.95) 0%, rgba(35, 35, 45, 0.95) 100%)',
    borderRadius: '32px',
    border: `4px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.3)'}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    padding: '24px 16px',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 40px rgba(0, 0, 0, 0.6), inset 0 2px 8px rgba(255, 255, 255, 0.05)'
      : '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.1)',
    position: 'relative',
    backdropFilter: 'blur(10px)'
  };
});

const TrafficLight: React.FC<TrafficLightProps> = ({
  status,
  score,
  recommendation,
  size = 'medium'
}) => {
  const theme = useTheme();

  // Normalize status to string
  const normalizedStatus = typeof status === 'number'
    ? (status === 3 ? 'green' : status === 2 ? 'yellow' : 'red')
    : status === 'grün' ? 'green' : status === 'gelb' ? 'yellow' : status === 'rot' ? 'red' : status;

  const lightSize = size === 'small' ? 28 : size === 'medium' ? 36 : 44;

  const getLightStyle = (color: string, isActive: boolean) => ({
    width: lightSize,
    height: lightSize,
    borderRadius: '50%',
    backgroundColor: isActive ? color : 'rgba(80, 80, 90, 0.3)',
    border: isActive
      ? `3px solid rgba(255, 255, 255, 0.4)`
      : `3px solid rgba(100, 100, 110, 0.3)`,
    boxShadow: isActive
      ? `0 0 25px ${color}, 0 0 50px ${color}aa, 0 0 75px ${color}66, inset 0 0 15px ${color}cc`
      : 'inset 0 2px 8px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.4s ease',
    animation: isActive ? `${pulse} 2s ease-in-out infinite, ${glow} 3s ease-in-out infinite` : 'none',
    position: 'relative' as const,
    '&::before': isActive ? {
      content: '""',
      position: 'absolute',
      top: '15%',
      left: '25%',
      width: '30%',
      height: '30%',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.6)',
      filter: 'blur(4px)'
    } : {}
  });

  const getStatusConfig = () => {
    switch (normalizedStatus) {
      case 'green':
        return {
          color: '#4CAF50',
          title: 'Empfohlen',
          description: 'Hybrid-Speicher sehr gut geeignet'
        };
      case 'yellow':
        return {
          color: '#FF9800',
          title: 'Prüfenswert',
          description: 'Hybrid-Speicher könnten sinnvoll sein'
        };
      case 'red':
      default:
        return {
          color: '#f44336',
          title: 'Nicht empfohlen',
          description: 'Hybrid-Speicher nicht optimal geeignet'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
      <TrafficLightContainer size={size}>
        {/* Red Light */}
        <Box sx={getLightStyle('#ff3333', normalizedStatus === 'red')} />

        {/* Yellow Light */}
        <Box sx={getLightStyle('#ffbb33', normalizedStatus === 'yellow')} />

        {/* Green Light */}
        <Box sx={getLightStyle('#44ff44', normalizedStatus === 'green')} />
      </TrafficLightContainer>

      <Box textAlign="center" maxWidth={240}>
        <Typography
          variant={size === 'large' ? 'h4' : size === 'medium' ? 'h5' : 'h6'}
          fontWeight="800"
          sx={{
            color: config.color,
            mb: 1.5,
            textShadow: theme.palette.mode === 'dark'
              ? `0 2px 20px ${config.color}66`
              : 'none'
          }}
        >
          {config.title}
        </Typography>

        {score !== undefined && score > 0 && (
          <Chip
            label={`${score}/100`}
            sx={{
              bgcolor: config.color + '22',
              color: config.color,
              fontWeight: 700,
              fontSize: size === 'large' ? '1.1rem' : '0.95rem',
              px: 2,
              py: size === 'large' ? 2.5 : 2,
              mb: 1.5,
              border: `2px solid ${config.color}66`,
              boxShadow: `0 4px 12px ${config.color}33`
            }}
          />
        )}

        <Typography
          variant={size === 'large' ? 'body1' : 'body2'}
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            fontWeight: 500
          }}
        >
          {recommendation || config.description}
        </Typography>
      </Box>
    </Box>
  );
};

export default TrafficLight;